import { Button, Form, Input, InputNumber } from 'antd';
import { useForm } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import FormList from 'antd/es/form/FormList';
import { DatePicker } from 'antd/lib';
import { ProviderSelectorContainer } from '../shared/provider-selector/provider-selector.containers';
import { map } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { useImperativeHandle } from 'react';
import { GetEnterpriseOrdersResult } from '../utils/api-client';
import { PlusOutlined } from '@ant-design/icons';

type EnterpriseOrderEditFormComponentProps = {
  formRef?: React.RefObject<any>;
  data: GetEnterpriseOrdersResult['data'][number];
};

export const EnterpriseOrderEditFormComponent: React.FC<
  EnterpriseOrderEditFormComponentProps
> = ({ formRef, data }) => {
  const [form] = useForm<{
    clientName: string;
    clientTaxId: string;
    contactInfo: string;
    deliveryAddress: string;
    deliveryDate: Dayjs;
    deliveryFee: number;
    discount: number;
    items: {
      providerId: string;
      name: string;
      quantity: number;
      price: number;
    }[];
  }>();

  useImperativeHandle(formRef, () => form, [form]);

  return (
    <Form
      form={form}
      preserve={false}
      initialValues={{
        ...data,
        deliveryDate: data.deliveryDate ? dayjs(data.deliveryDate) : undefined,
      }}
    >
      <FormItem
        label="訂餐日期"
        name="deliveryDate"
        rules={[{ required: true, message: '請輸入訂餐日期' }]}
      >
        <DatePicker
          placeholder="請選擇訂餐日期"
          className="z-50 w-full"
          showHour
          showMinute
          showTime
          getPopupContainer={(triggerNode) => {
            return triggerNode.parentElement
              ? triggerNode.parentElement
              : triggerNode;
          }}
        />
      </FormItem>
      <div className="flex gap-2 *:flex-grow *:basis-0">
        <FormItem
          label="運費"
          name="deliveryFee"
          rules={[
            { required: true, message: '請輸入運費' },
            { type: 'integer', message: '運費必須是整數' },
          ]}
        >
          <InputNumber placeholder="請輸入運費" min={0} className="w-full" />
        </FormItem>
        <FormItem
          label="折扣(%)"
          name="discount"
          rules={[{ required: true, message: '請輸入折扣' }]}
        >
          <InputNumber
            placeholder="請輸入折扣"
            min={0}
            max={100}
            className="w-full"
            step={5}
          />
        </FormItem>
      </div>
      <div className="flex gap-2 *:flex-grow">
        <FormItem
          label="客戶名稱"
          name="clientName"
          rules={[{ required: true, message: '請輸入客戶名稱' }]}
        >
          <Input placeholder="請輸入客戶名稱" />
        </FormItem>
        <FormItem
          label="統一編號"
          name="clientTaxId"
          rules={[
            {
              required: true,
              message: '請輸入統一編號',
            },
            {
              pattern: /^[0-9]{8}$/,
              message: '統一編號格式不正確',
            },
          ]}
        >
          <Input placeholder="請輸入統一編號" />
        </FormItem>
      </div>
      <FormItem
        label="訂餐地址"
        name="deliveryAddress"
        rules={[{ required: true, message: '請輸入訂餐地址' }]}
      >
        <Input placeholder="請輸入訂餐地址" />
      </FormItem>
      <FormItem
        label="聯絡窗口"
        name="contactInfo"
        rules={[{ required: true, message: '請輸入聯絡窗口' }]}
      >
        <Input placeholder="請輸入聯絡窗口" />
      </FormItem>
      <FormList name="items">
        {(fields, { add, remove }) => {
          return (
            <>
              {map(fields, ({ key, name, ...restField }) => (
                <div key={key} className="flex flex-col">
                  <div>
                    <span className="font-bold">餐點 {name + 1}</span>
                    <Button type="link" onClick={() => remove(name)}>
                      刪除
                    </Button>
                  </div>
                  <div className="flex gap-2 *:flex-grow *:basis-0">
                    <ProviderSelectorContainer
                      isRequired
                      name={[name, 'providerId']}
                    />
                    <FormItem
                      {...restField}
                      name={[name, 'name']}
                      label="品項名稱"
                      rules={[{ required: true, message: '請輸入品項名稱' }]}
                      shouldUpdate
                    >
                      <Input
                        placeholder="請輸入品項名稱"
                        className="flex-grow"
                      />
                    </FormItem>
                  </div>
                  <div className="flex gap-2 *:flex-grow *:basis-0">
                    <FormItem
                      {...restField}
                      name={[name, 'price']}
                      label="金額"
                      className="flex-shrink-0"
                      rules={[{ required: true, message: '請輸入金額' }]}
                    >
                      <InputNumber
                        placeholder="請輸入金額"
                        min={1}
                        className="w-full"
                      />
                    </FormItem>
                    <FormItem
                      {...restField}
                      name={[name, 'quantity']}
                      label="數量"
                      className="flex-shrink-0"
                      rules={[{ required: true, message: '請輸入數量' }]}
                    >
                      <InputNumber
                        placeholder="數量"
                        min={1}
                        className="w-full"
                      />
                    </FormItem>
                  </div>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                新增餐點
              </Button>
            </>
          );
        }}
      </FormList>
    </Form>
  );
};
